import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  useTheme,
  alpha,
  Paper,
  Tabs,
  Tab,
  Divider,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventIcon from '@mui/icons-material/Event';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareIcon from '@mui/icons-material/Compare';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DayIcon from '@mui/icons-material/WbSunny';
import WeekIcon from '@mui/icons-material/DateRange';
import MonthIcon from '@mui/icons-material/CalendarViewMonth';
import YearIcon from '@mui/icons-material/CalendarToday';
import SavingsIcon from '@mui/icons-material/Savings';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

// Chart components
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ReferenceArea
} from 'recharts';

// Import from our business service
import { 
  getBusiness, 
  getEnergyUsage,
  getSectorData,
  getEnergyInsights,
  getUsageForecast
} from '../services/business';

import { 
  Business, 
  EnergyUsageData,
  SectorStats,
  EnergyInsightsData,
  DailyUsageData,
  WeeklyUsageData,
  MonthlyUsageData,
  YearlyUsageData,
  UsageForecastData
} from '../services/business';

import PeakDemandAnalysis from '../components/insights/PeakDemandAnalysis';
import UsagePatterns from '../components/insights/UsagePatterns';
import UsageForecast from '../components/insights/UsageForecast';

interface InsightsProps {
  businessId: string;
  timeFrame?: number;
}

// Entry interface
interface ZoneEntry {
  zone: string;
  percentage: number;
  kWh: number;
}

// Time period type for usage graphs
type TimePeriod = 'day' | 'week' | 'month' | 'year';

// Add a new type for forecast durations
type ForecastDuration = '1-month' | '3-month' | '1-year';

const Insights = ({ businessId, timeFrame = 12 }: InsightsProps) => {
  const theme = useTheme();
  
  // States for data
  const [business, setBusiness] = useState<Business | null>(null);
  const [energyUsage, setEnergyUsage] = useState<EnergyUsageData | null>(null);
  const [sectorData, setSectorData] = useState<SectorStats | null>(null);
  const [energyInsights, setEnergyInsights] = useState<EnergyInsightsData | null>(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [savingsSlider, setSavingsSlider] = useState<number>(0);
  const [benchmarkMetric, setBenchmarkMetric] = useState<string>('energy');

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch business info
        const businessData = await getBusiness(businessId);
        setBusiness(businessData);
        
        // Fetch energy usage data (for features still using mock data)
        const usageData = await getEnergyUsage(businessId);
        setEnergyUsage(usageData);
        
        // Fetch energy insights data
        const insightsData = await getEnergyInsights(businessId);
        setEnergyInsights(insightsData);
        
        // Fetch sector data
        if (businessData) {
          const sectorStats = await getSectorData(businessData.type);
          setSectorData(sectorStats);
        }
        
        setError('');
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error loading data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [businessId, timeFrame]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle time period change
  const handleTimePeriodChange = (_event: React.MouseEvent<HTMLElement>, newPeriod: TimePeriod | null) => {
    if (newPeriod !== null) {
      setTimePeriod(newPeriod);
    }
  };
  
  // Handle savings slider change
  const handleSavingsChange = (_event: Event, newValue: number | number[]) => {
    setSavingsSlider(newValue as number);
  };
  
  // Handle benchmark metric change
  const handleBenchmarkChange = (event: SelectChangeEvent) => {
    setBenchmarkMetric(event.target.value);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  // Format submetering data for pie chart
  const submeteringData = energyUsage?.submetering || [];
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    alpha(theme.palette.primary.main, 0.7),
    alpha(theme.palette.secondary.main, 0.7),
    alpha(theme.palette.success.main, 0.7),
  ];
  
  // Generate time-based usage data
  const generateTimeData = () => {
    if (!energyInsights) {
      console.warn("No energy insights data available");
      return [];
    }
    
    try {
      // Check if we're using the new data structure
      if ('daily_latest' in energyInsights && energyInsights.daily_latest) {
        console.log("Using new data structure");
        
        switch(timePeriod) {
          case 'day':
            return energyInsights.daily_latest.map(item => ({
              time: new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              kwh: item.kwh,
              hour: new Date(item.timestamp).getHours(),
              isOperational: isOperationalHour(new Date(item.timestamp).getHours())
            }));
          case 'week':
            return energyInsights.weekly_latest_hourly.map(item => {
              const date = new Date(item.timestamp);
              return {
                time: `${date.toLocaleDateString([], { weekday: 'short' })} ${date.toLocaleTimeString([], { hour: '2-digit' })}`,
                kwh: item.kwh,
                hour: date.getHours(),
                isOperational: isOperationalHour(date.getHours())
              };
            });
          case 'month':
            return energyInsights.monthly_latest_daily.map(item => {
              const date = new Date(item.timestamp);
              return {
                week: `Day ${date.getDate()}`,
                weekNumber: date.getDate(),
                kwh: item.kwh,
                isOperational: true
              };
            });
          case 'year':
            return energyInsights.yearly_latest_monthly.current_year.map(item => {
              return {
                month: item.month,
                monthNumber: getMonthNumber(item.month),
                kwh: item.kwh,
                previousYear: getPreviousYearValue(item.month, energyInsights.yearly_latest_monthly.previous_year),
                isOperational: true
              };
            });
          default:
            return energyInsights.monthly_latest_daily.map(item => {
              const date = new Date(item.timestamp);
              return {
                week: `Day ${date.getDate()}`,
                weekNumber: date.getDate(),
                kwh: item.kwh,
                isOperational: true
              };
            });
        }
      }
      
      // Fallback to old data structure if needed
      if ('usagePatterns' in energyInsights && energyInsights.usagePatterns) {
        console.log("Using legacy data structure");
        
        switch(timePeriod) {
          case 'day':
            return energyInsights.usagePatterns.daily || [];
          case 'week':
            return energyInsights.usagePatterns.weekly || [];
          case 'month':
            return energyInsights.usagePatterns.monthly || [];
          case 'year':
            return energyInsights.usagePatterns.yearly || [];
          default:
            return energyInsights.usagePatterns.monthly || [];
        }
      }
      
      console.warn("Neither new nor legacy data structure found in energyInsights data");
      return [];
    } catch (error) {
      console.error(`Error processing ${timePeriod} data:`, error);
      return [];
    }
  };
  
  // Helper function to find previous year value for a given month
  const getPreviousYearValue = (month: string, previousYearData: any[] = []): number => {
    try {
      const prevYearItem = previousYearData.find(item => item.month === month);
      return prevYearItem ? prevYearItem.kwh : 0;
    } catch (error) {
      console.error("Error finding previous year value:", error);
      return 0;
    }
  };
  
  // Helper function to determine if it's an operational hour (7am-9pm)
  const isOperationalHour = (hour: number): boolean => {
    return hour >= 7 && hour <= 21;
  };
  
  // Helper function to get month number from abbreviation
  const getMonthNumber = (monthAbbr: string): number => {
    const months: Record<string, number> = {
      'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
      'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
    };
    return months[monthAbbr] || 1;
  };

  // Get time period label
  const getTimeLabel = () => {
    switch(timePeriod) {
      case 'day': return 'Daily';
      case 'week': return 'Weekly';
      case 'month': return 'Monthly';
      case 'year': return 'Yearly';
      default: return 'Monthly';
    }
  };
  
  // Generate data for bill forecasting
  const generateBillForecastData = () => {
    const forecast = energyUsage?.billForecast;
    if (!forecast) return [];
    
    // Calculate projected savings based on slider
    const savingsPercentage = savingsSlider / 100;
    const savingsAmount = forecast.forecastNextMonth * savingsPercentage;
    const projectedBill = forecast.forecastNextMonth - savingsAmount;
    
    return [
      { name: 'Current Bill', amount: forecast.currentBill },
      { name: 'Forecast (No Changes)', amount: forecast.forecastNextMonth },
      { name: 'Projected (With Changes)', amount: projectedBill }
    ];
  };
  
  // Generate time series data for bill forecasting
  const generateBillTimeSeriesData = () => {
    const monthly = energyUsage?.monthly || [];
    const forecast = energyUsage?.billForecast;
    
    if (!forecast || monthly.length === 0) return [];
    
    // Past 6 months of actual bills
    const pastBills = monthly.slice(-6).map((item, index) => ({
      name: item.month,
      type: 'Historical',
      amount: item.cost,
      forecastAmount: null
    }));
    
    // Next 3 months of forecasted bills
    const currentMonth = monthly[monthly.length - 1];
    const nextMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonthIndex = nextMonths.findIndex(m => m === currentMonth.month);
    
    const forecastedBills = [];
    for (let i = 1; i <= 3; i++) {
      const monthIndex = (currentMonthIndex + i) % 12;
      const growthRate = 1 + (i * 0.02); // 2% growth per month
      
      // Calculate slight variations for future months
      let forecastedAmount;
      if (i === 1) {
        forecastedAmount = forecast.forecastNextMonth;
      } else if (i === 2) {
        forecastedAmount = forecast.forecastNextMonth * growthRate;
      } else {
        forecastedAmount = forecast.forecastNextMonth * growthRate * 1.01;
      }
      
      // Calculate projected amount with savings
      const savingsPercentage = savingsSlider / 100;
      const savingsAmount = forecastedAmount * savingsPercentage;
      const projectedAmount = forecastedAmount - savingsAmount;
      
      forecastedBills.push({
        name: nextMonths[monthIndex],
        type: 'Forecast',
        amount: null,
        forecastAmount: forecastedAmount,
        projectedAmount: projectedAmount
      });
    }
    
    return [...pastBills, ...forecastedBills];
  };
  
  // Generate extended forecast data for line chart (12-month horizon)
  const generateExtendedForecastData = () => {
    const monthly = energyUsage?.monthly || [];
    const forecast = energyUsage?.billForecast;
    
    if (!forecast || monthly.length === 0) return [];
    
    // Past 6 months of actual bills
    const pastBills = monthly.slice(-6).map(item => ({
      month: item.month,
      historical: item.cost,
      forecast: null,
      withSavings: null
    }));
    
    // Next 12 months of forecasted bills
    const currentMonth = monthly[monthly.length - 1];
    const nextMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonthIndex = nextMonths.findIndex(m => m === currentMonth.month);
    
    const forecastedBills = [];
    for (let i = 1; i <= 12; i++) {
      const monthIndex = (currentMonthIndex + i) % 12;
      
      // Create seasonal pattern (higher in summer/winter, lower in spring/fall)
      const monthFactor = 1 + 0.15 * Math.sin((i - 3) * Math.PI / 6);
      
      // Add slight upward trend (0.5% per month)
      const trendFactor = 1 + (i * 0.005);
      
      // Calculate forecast with seasonal and trend factors
      const forecastAmount = forecast.forecastNextMonth * monthFactor * trendFactor;
      
      // Apply savings percentage from slider
      const savingsPercentage = savingsSlider / 100;
      const withSavingsAmount = forecastAmount * (1 - savingsPercentage);
      
      forecastedBills.push({
        month: nextMonths[monthIndex],
        historical: null,
        forecast: forecastAmount,
        withSavings: withSavingsAmount
      });
    }
    
    return [...pastBills, ...forecastedBills];
  };
  
  // Generate benchmark data
  const generateBenchmarkData = () => {
    const sector = sectorData;
    if (!sector) return [];
    
    // Get the name based on the business id
    const findBusinessName = (id: string) => {
      const business = sector.businesses.find(b => b.id === id);
      return business ? business.name : 'Unknown';
    };
    
    // Sort businesses by score
    const sortedBusinesses = [...sector.businesses].sort((a, b) => b.score - a.score);
    
    // Create radar chart data
    return [
      { 
        subject: 'Energy Efficiency', 
        A: 80, // Your business
        B: sector.averageScore, // Sector average 
        fullMark: 100 
      },
      { 
        subject: 'Peak Demand', 
        A: 75, 
        B: 65,
        fullMark: 100 
      },
      { 
        subject: 'Renewable Energy', 
        A: business?.renewablePercentage || 0, 
        B: sector.averageRenewable,
        fullMark: 100 
      },
      { 
        subject: 'Cost Efficiency', 
        A: 70, 
        B: 60,
        fullMark: 100 
      },
      { 
        subject: 'Energy Baseline', 
        A: 85, 
        B: 70,
        fullMark: 100 
      }
    ];
  };

  // Time-based keys and labels
  const timeKey = (() => {
    switch(timePeriod) {
      case 'day': return 'time';
      case 'week': return 'time';
      case 'month': return 'week';
      case 'year': return 'month';
      default: return 'time';
    }
  })();
  
  const valueKey = "kwh";

  // Safe data accessor to prevent crashes when changing time periods
  const getSafeTimeData = () => {
    try {
      const data = generateTimeData();
      
      // Make sure data exists and has the expected structure
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.warn(`No data available for time period: ${timePeriod}`);
        return [];
      }
      
      // For safety, verify the first item has the expected structure
      const sampleItem = data[0];
      if (!sampleItem || typeof sampleItem !== 'object') {
        console.warn(`Invalid data format for ${timePeriod}`);
        return [];
      }
      
      // Since different time periods have different properties, we can't do a strict check
      // but we should at least have either a time/week/month field and kwh
      const hasValidStructure = ('time' in sampleItem || 'week' in sampleItem || 'month' in sampleItem) 
                                && 'kwh' in sampleItem;
      
      if (!hasValidStructure) {
        console.warn(`Data for ${timePeriod} missing required keys for display`);
        return [];
      }
      
      return data;
    } catch (err) {
      console.error(`Error processing ${timePeriod} data:`, err);
      return [];
    }
  };

  // Get the safe data for display
  const timeData = getSafeTimeData();
  const billForecastData = generateBillForecastData();
  const benchmarkData = generateBenchmarkData();

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Business Energy Insights
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Detailed analytics and insights about your building's energy consumption patterns
        </Typography>
      </Box>
      
      {/* Main Content */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 4, 
          overflow: 'hidden', 
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', 
          mb: 4,
          transition: theme.transitions.create(['box-shadow', 'transform'], {
            duration: theme.transitions.duration.standard
          }),
          '&:hover': {
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-4px)',
          }
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            centered
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                py: 2,
                flexGrow: 1,
                maxWidth: 'none',
                transition: theme.transitions.create(['color', 'background-color'], {
                  duration: theme.transitions.duration.short
                }),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  color: theme.palette.primary.main,
                },
              },
              width: '100%'
            }}
          >
            <Tab 
              label="Usage Patterns" 
              icon={<ElectricBoltIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Usage Forecasting" 
              icon={<TrendingUpIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Peak Demand" 
              icon={<TrendingDownIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 3 }}>
          {/* Usage Patterns Tab */}
          {tabValue === 0 && (
            <UsagePatterns />
          )}
          
          {/* Usage Forecasting Tab */}
          {tabValue === 1 && (
            <UsageForecast businessId={businessId} />
          )}
          
          {/* Peak Demand Tab */}
          {tabValue === 2 && (
            <PeakDemandAnalysis />
          )}
        </Box>
      </Paper>
      
      {/* Export Section */}
      <Box sx={{ textAlign: 'right' }}>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<DownloadIcon />}
          sx={{
            transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
              duration: theme.transitions.duration.short
            }),
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
            }
          }}
        >
          Export Insights Report
        </Button>
      </Box>
    </Container>
  );
};

export default Insights; 